import { useEffect, useState } from 'react';
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import maskString from '@/core/utils/maskString';

type TDefault = {
  name?: string;
  placeholder?: string;
  value: string;
  setIsValid?: Dispatch<SetStateAction<boolean>>;
  setValue: Dispatch<SetStateAction<string>>;
};

type TValidate = {
  errorMessage?: string;
  mask?: string;
  maxLength?: number;
  replace?: RegExp;
  showCounter?: boolean;
  validate?: RegExp;
};

type ICustomProps = TDefault & TValidate & { type: 'custom' };
type ITypeProps = TDefault & { type: 'email' | 'phone' };
export type IFieldProps = ICustomProps | ITypeProps;

type IProps = TDefault & TValidate & { type: 'custom' | 'email' | 'phone' };

const useField = (props: IProps) => {
  const [errors, setErrors] = useState<string[]>(['xsss']);
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [oldProps, setOldProps] = useState<IProps>(props);
  const [status, setStatus] = useState<'ERROR' | 'SUCCESS' | undefined>();

  const [counter, setCounter] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<IProps['errorMessage']>();
  const [maxLength, setMaxLength] = useState<IProps['maxLength']>();
  const [name, setName] = useState<IProps['name']>();
  const [placeholder, setPlaceholder] = useState<IProps['placeholder']>();
  const [replace, setReplace] = useState<IProps['replace']>();
  const [showCounter, setShowCounter] = useState<IProps['showCounter']>();
  const [validate, setValidate] = useState<IProps['validate']>();
  const [value, setValue] = useState<string>('');

  const types: Record<ITypeProps['type'], TValidate> = {
    email: {
      errorMessage: 'Ingresa un correo electrónico valido.',
      validate: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
    },
    phone: {
      errorMessage: 'Ingresa un número de celular válido.',
      maxLength: 9,
      replace: /\D/g,
      showCounter: true,
      validate: /^9\d{8}$/
    }
  };

  const onValue = (v: string) => {
    const resultReplace = replace ? v.replace(replace, '') : v;
    const result = resultReplace.slice(0, maxLength);

    setErrors([]);
    setStatus(undefined);

    if (result.length > 0) {
      if (validate && !validate.test(result)) {
        setErrors([errorMessage ?? 'Ingresa un valor válido.']);
        setStatus('ERROR');
        if (props.setIsValid) props.setIsValid(false);
      } else if (maxLength && maxLength !== result.length) {
        setErrors([errorMessage ?? 'Ingresa un valor válido.']);
        setStatus('ERROR');
        if (props.setIsValid) props.setIsValid(false);
      } else {
        setErrors([]);
        setStatus('SUCCESS');
        if (props.setIsValid) props.setIsValid(true);
      }
    }

    const mask = props.mask ?? types[props.type as ITypeProps['type']].mask;

    setValue(isFocus ? result : maskString(result, mask));
    props.setValue(result);
  };

  const onBlur = () => {
    setIsFocus(false);
  };

  const onFocus = () => {
    setIsFocus(true);
  };

  const onInput = (e: FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    onValue(target.value);
  };

  const onDefault = (init: boolean) => {
    if (!init) {
      setOldProps(props);
    }

    setCounter(props.value.length);
    setErrorMessage(
      props.errorMessage ?? types[props.type as ITypeProps['type']].errorMessage
    );
    setMaxLength(
      props.maxLength ?? types[props.type as ITypeProps['type']].maxLength
    );
    setName(props.name);
    setPlaceholder(props.placeholder);
    setReplace(
      props.replace || types[props.type as ITypeProps['type']].replace
    );
    setShowCounter(
      props.showCounter || types[props.type as ITypeProps['type']].showCounter
    );
    setValidate(
      props.validate || types[props.type as ITypeProps['type']].validate
    );
  };

  useEffect(() => {
    onDefault(true);
    onValue(props.value);
  }, [isFocus]);

  useEffect(() => {
    if (JSON.stringify(oldProps) !== JSON.stringify(props)) {
      onValue(props.value);
      onDefault(false);
    }
  }, [props]);

  return {
    counter,
    errors,
    maxLength,
    showCounter,
    field: {
      name,
      placeholder,
      status,
      value,
      onBlur,
      onFocus,
      onInput
    }
  };
};

export default useField;
